import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { cmsText } from "@/lib/cms";
import { thumbUrl } from "@/lib/product";
import { Cta, Reveal, SectionHeading } from "@/components/blocks/shared";
import { ShelfBlock } from "@/components/blocks/Shelf";
import { BrandStoryBlock } from "@/components/blocks/BrandStory";
import { FaqBlock, StatsBlock, TestimonialsBlock, BenefitsBlock } from "@/components/blocks/Content";
import { VideoBlock } from "@/components/blocks/Video";

const Hero = ({ block, T }) => (
  <section className="relative overflow-hidden border-b bg-background" data-testid="block-hero">
    <div className="absolute inset-0 hero-noise" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
      <div className="lg:col-span-6">
        <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">{T(block.titel)}</h1>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">{T(block.subtitel)}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Cta label={T(block.cta_label)} link={block.cta_link} testId="block-hero-cta" arrow />
          <Cta label={T(block.cta2_label)} link={block.cta2_link} variant="outline" testId="block-hero-cta2" />
        </div>
      </div>
      {block.afbeelding && (
        <div className="lg:col-span-6">
          <img src={thumbUrl(block.afbeelding, 1200)} alt={T(block.titel)} className="aspect-[4/3] w-full rounded-3xl object-cover shadow-[0_30px_60px_-30px_hsl(30_20%_20%/0.5)]" data-testid="block-hero-image" />
        </div>
      )}
    </div>
  </section>
);

const RichText = ({ block, T }) => (
  <section className="border-b bg-background" data-testid="block-richtext">
    <Reveal className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
      <div className="lg:col-span-4">
        <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} />
      </div>
      <div className="whitespace-pre-line text-base leading-relaxed text-muted-foreground lg:col-span-7 lg:col-start-6">{T(block.tekst)}</div>
    </Reveal>
  </section>
);

const ImageText = ({ block, T }) => {
  const links = block.positie === "links";
  return (
    <section className="border-b bg-sand-light" data-testid="block-afbeelding-tekst">
      <Reveal className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:px-8 lg:py-24">
        <div className={`lg:col-span-6 ${links ? "lg:order-2" : ""}`}>
          <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} />
          <div className="mt-5 whitespace-pre-line text-base leading-relaxed text-muted-foreground">{T(block.tekst)}</div>
          <div className="mt-7"><Cta label={T(block.cta_label)} link={block.cta_link} variant="outline" testId="block-afbeelding-tekst-cta" arrow /></div>
        </div>
        {block.afbeelding && (
          <div className={`lg:col-span-6 ${links ? "lg:order-1" : ""}`}>
            <div className="overflow-hidden rounded-3xl bg-secondary shadow-[0_30px_60px_-30px_hsl(30_20%_20%/0.45)]">
              <img src={thumbUrl(block.afbeelding, 1200)} alt={T(block.titel)} className="aspect-[4/3] w-full object-cover" />
            </div>
          </div>
        )}
      </Reveal>
    </section>
  );
};

const Grid = ({ block, T, lang }) => {
  const items = block.items || [];
  return (
    <section className="border-b bg-background" data-testid="block-grid">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <Reveal><SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} /></Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
          {items.map((item, i) => {
            const groot = items.length >= 3 && i === 0;
            return (
              <Reveal key={i} delay={i * 0.06} className={groot ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"}>
                <article className="card-lift group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-card" data-testid="block-grid-item">
                  {item.afbeelding && (
                    <div className={`overflow-hidden bg-muted ${groot ? "aspect-[4/3] lg:h-full lg:aspect-auto lg:absolute lg:inset-0" : "aspect-[16/10]"}`}>
                      <img src={thumbUrl(item.afbeelding, 720)} alt={cmsText(item.titel, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" data-testid="block-grid-image" />
                      {groot && <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" aria-hidden="true" />}
                    </div>
                  )}
                  <div className={`flex flex-1 flex-col p-6 ${groot ? "lg:relative lg:mt-auto lg:justify-end lg:text-ink-foreground lg:min-h-[420px]" : ""}`}>
                    <h3 className="font-display text-lg font-bold tracking-tight sm:text-xl" data-testid="block-grid-title">{cmsText(item.titel, lang)}</h3>
                    <p className={`mt-2 text-sm leading-relaxed ${groot ? "text-muted-foreground lg:text-ink-foreground/75" : "text-muted-foreground"}`}>{cmsText(item.tekst, lang)}</p>
                    {item.link && (
                      <Cta label={cmsText(item.cta_label, lang) || T(block.item_cta_label)} link={item.link} variant="ghost" className="mt-4 w-fit px-0 hover:bg-transparent" testId={`block-grid-cta-${i}`} />
                    )}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CtaBanner = ({ block, T }) => (
  <section className="section-ink relative overflow-hidden grain" data-testid="block-cta-banner">
    <div className="absolute -left-20 -bottom-40 h-[380px] w-[380px] rounded-full bg-primary-light/20 blur-3xl" aria-hidden="true" />
    <Reveal className="relative mx-auto flex max-w-7xl flex-col gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20">
      <div className="max-w-2xl">
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{T(block.titel)}</h2>
        <p className="mt-4 text-base leading-relaxed text-ink-foreground/70">{T(block.tekst)}</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Cta label={T(block.cta_label)} link={block.cta_link} testId="block-cta-primary" arrow className="bg-ink-foreground text-ink hover:bg-white" />
        <Cta label={T(block.cta2_label)} link={block.cta2_link} variant="outline" testId="block-cta-secondary" className="border-white/25 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground" />
      </div>
    </Reveal>
  </section>
);

const Steps = ({ block, T }) => (
  <section className="border-b bg-background" data-testid="block-stappen">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <Reveal><SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} /></Reveal>
      <ol className="mt-12 grid gap-6 md:grid-cols-3">
        {(block.items || []).map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <li className="card-lift relative h-full rounded-2xl border bg-card p-7" data-testid={`step-${i}`}>
              <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{T(s.titel)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{T(s.tekst)}</p>
              {i < (block.items || []).length - 1 && <ArrowUpRight className="absolute right-6 top-6 h-4 w-4 text-muted-foreground/40 md:hidden" aria-hidden="true" />}
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const RENDERERS = {
  schap: ShelfBlock,
  hero: Hero,
  richtext: RichText,
  "afbeelding-tekst": ImageText,
  "afbeeldingen-grid": Grid,
  "cta-banner": CtaBanner,
  stappen: Steps,
  "merk-toggle": BrandStoryBlock,
  voordelen: BenefitsBlock,
  faq: FaqBlock,
  video: VideoBlock,
  cijfers: StatsBlock,
  testimonials: TestimonialsBlock,
};

export const Block = ({ block }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const T = (f) => cmsText(f, lang);
  const Renderer = block && RENDERERS[block.type];
  return Renderer ? <Renderer block={block} T={T} lang={lang} /> : null;
};

export const BLOCK_TYPES = Object.keys(RENDERERS);
